import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { register } from "../../Firebase/authService";
import { getAuth, updateProfile } from "firebase/auth";
import "./Signup.css";

const API_URL =
  process.env.REACT_APP_API_URL ||
  "https://automotive-backend-production.up.railway.app";

function SignUp() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const validateForm = () => {
    if (!fullName.trim()) {
      setErrorMsg("Please enter your full name.");
      return false;
    }

    if (!email.trim()) {
      setErrorMsg("Please enter your email address.");
      return false;
    }

    if (password.length < 6) {
      setErrorMsg("Password must be at least 6 characters long.");
      return false;
    }

    if (password !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return false;
    }

    return true;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!validateForm()) return;

    try {
      setLoading(true);

      const userCredential = await register(email, password);
      const user = userCredential.user;

      const auth = getAuth();

      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          displayName: fullName.trim(),
        });
      }

      const idToken = await user.getIdToken(true);

      const response = await fetch(`${API_URL}/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: user.uid,
          email: user.email,
          name: fullName.trim(),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Backend error");
      }

      const respJson = await response.json();
      console.log("Backend response:", respJson);

      setSuccessMsg("Account created successfully. Redirecting to login...");

      setFullName("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");

      setTimeout(() => {
        navigate("/login");
      }, 1200);
    } catch (err) {
      console.error("Sign-up error:", err);

      let message = err.message || "Failed to register.";

      if (message.includes("auth/email-already-in-use")) {
        message = "This email is already registered. Please login instead.";
      } else if (message.includes("auth/invalid-email")) {
        message = "Please enter a valid email address.";
      } else if (message.includes("auth/weak-password")) {
        message = "Password is too weak. Please use a stronger password.";
      }

      setErrorMsg(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-card">
        <div className="signup-brand">
          <div className="signup-logo-circle">
            <i className="bi bi-person-plus-fill"></i>
          </div>
          <h2>Create Account</h2>
          <p>Join Shipment Solutions LLC and manage your auction experience.</p>
        </div>

        {errorMsg && (
          <div className="signup-alert error-alert">
            <i className="bi bi-exclamation-circle-fill"></i>
            <span>{errorMsg}</span>
          </div>
        )}

        {successMsg && (
          <div className="signup-alert success-alert">
            <i className="bi bi-check-circle-fill"></i>
            <span>{successMsg}</span>
          </div>
        )}

        <form onSubmit={handleSignUp} className="signup-form">
          <div className="form-group">
            <label htmlFor="fullName">Full Name</label>
            <div className="input-wrapper">
              <i className="bi bi-person"></i>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-wrapper">
              <i className="bi bi-envelope"></i>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <i className="bi bi-lock"></i>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
              </button>
            </div>
            <small className="password-hint">
              Use at least 6 characters for your password.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="input-wrapper">
              <i className="bi bi-shield-lock"></i>
              <input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
              />
            </div>
          </div>

          <button type="submit" className="signup-submit-btn" disabled={loading}>
            {loading ? (
              <>
                <span className="signup-spinner"></span>
                Creating Account...
              </>
            ) : (
              <>
                Create Account
                <i className="bi bi-arrow-right"></i>
              </>
            )}
          </button>
        </form>

        <div className="signup-footer">
          <p>
            Already have an account? <Link to="/login">Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignUp;